/**
 * The clipboard payload for one assistant message: neutral semantic HTML plus
 * the markdown that backs it.
 *
 * Both flavours derive from the SOURCE markdown, never from the rendered or
 * typeset tree — the source-model separation rule the editorial layer is built
 * on. Rendering the live component tree instead would emit Tailwind classes
 * (which no paste target reads), interactive citation chips, and buttons.
 */

import type { Element, Root } from "hast";
import { toHtml } from "hast-util-to-html";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, {
    defaultSchema,
    type Options as SanitizeOptions,
} from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

import type { ICitationResolution } from "@/api/chat/chat.types";

import {
    resolveCitationLinks,
    type CitationLinkContext,
} from "./citation-links";
import { removeEmojiSequences, stripAllEmojis } from "./strip-emojis-for-copy";

const RESOURCE_TAGS = new Set(["img", "picture", "source"]);
const URL_PROPERTIES = new Set([
    "action",
    "cite",
    "formAction",
    "href",
    "longDesc",
    "poster",
    "src",
    "srcSet",
    "useMap",
]);

const CLIPBOARD_SCHEMA: SanitizeOptions = {
    ...defaultSchema,
    tagNames: defaultSchema.tagNames?.filter(
        (tagName) => !RESOURCE_TAGS.has(tagName)
    ),
    attributes: Object.fromEntries(
        Object.entries(defaultSchema.attributes ?? {}).map(
            ([tagName, properties]) => [
                tagName,
                properties.filter(
                    (property) =>
                        (typeof property === "string"
                            ? property
                            : property[0]) !== "className"
                ),
            ]
        )
    ),
};

const neutralizeClipboardResources =
    () =>
    (tree: Root): void => {
        const stack: (Root | Element)[] = [tree];

        while (stack.length > 0) {
            const parent = stack.pop();
            if (parent === undefined) continue;

            const children: Element["children"] = [];
            for (const child of parent.children) {
                if (child.type === "doctype") continue;
                if (child.type !== "element") {
                    children.push(child);
                    continue;
                }

                if (child.tagName === "img") {
                    const alt = child.properties?.alt;
                    if (typeof alt === "string" && alt.length > 0) {
                        children.push({ type: "text", value: alt });
                    }
                    continue;
                }

                if (child.tagName === "source") continue;
                if (child.tagName === "picture") {
                    child.tagName = "span";
                    child.properties = {};
                }

                children.push(child);
                stack.push(child);
            }
            parent.children = children;
        }
    };

/**
 * Sanitize sits second-to-last and earns its place three ways: it drops the
 * `button://` and `link://` hrefs the model authors — which open nothing
 * outside the app — while keeping their label text, it neutralises the raw
 * HTML Polly does occasionally emit, and it strips `style`, which is why the
 * table pass below runs after it rather than as a plugin.
 *
 * Frozen so one processor serves every message; every plugin here is
 * synchronous, which is what lets `runSync` build the payload inside the click
 * gesture that authorises the clipboard write.
 */
const PROCESSOR = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(neutralizeClipboardResources)
    .use(rehypeSanitize, CLIPBOARD_SCHEMA)
    .freeze();

// Word, Outlook and Docs honour inline styles and nothing else — no stylesheet
// travels on the clipboard — and a table with no cell borders pastes as an
// invisible grid. Neutral grey rather than brand colour: the pasted answer
// should take on the destination document's typography.
const TABLE_STYLE = "border-collapse:collapse";
const CELL_STYLE = "border:1px solid #999999;padding:6px 10px";

const ALIGNMENTS: ReadonlySet<string> = new Set(["left", "center", "right"]);

const styleFor = (node: Element): string | undefined => {
    if (node.tagName === "table") return TABLE_STYLE;
    if (node.tagName !== "th" && node.tagName !== "td") return undefined;

    // GFM alignment survives sanitize as the `align` attribute. Word reads
    // that; Docs reads `text-align`. Emitting both costs nothing.
    const align = node.properties?.align;
    return typeof align === "string" && ALIGNMENTS.has(align)
        ? `${CELL_STYLE};text-align:${align}`
        : CELL_STYLE;
};

const scrubProperties = (node: Element): void => {
    for (const [name, value] of Object.entries(node.properties ?? {})) {
        const strip = URL_PROPERTIES.has(name)
            ? removeEmojiSequences
            : stripAllEmojis;

        if (typeof value === "string") {
            node.properties[name] = strip(value);
        } else if (Array.isArray(value)) {
            node.properties[name] = value.map((item) =>
                typeof item === "string" ? strip(item) : item
            );
        }
    }
};

/** Avoid recursion because authored nesting can exceed the call stack. */
const applyClipboardFormatting = (tree: Root): void => {
    const stack: (Root | Element)[] = [tree];
    const seen = new Set<Root | Element>();

    while (stack.length > 0) {
        const node = stack.pop();
        if (node === undefined || seen.has(node)) continue;
        seen.add(node);

        if (node.type === "element") {
            scrubProperties(node);
            const style = styleFor(node);
            if (style !== undefined) {
                node.properties = { ...node.properties, style };
            }
        }

        for (const child of node.children) {
            if (child.type === "text") {
                child.value = stripAllEmojis(child.value);
            } else if (child.type === "element") {
                stack.push(child);
            }
        }
    }
};

export const markdownToClipboardHtml = (markdown: string): string => {
    const tree = PROCESSOR.runSync(PROCESSOR.parse(markdown)) as Root;
    applyClipboardFormatting(tree);
    return toHtml(tree);
};

export interface RichCopyPayload {
    readonly text: string;
    /** Absent when the HTML could not be built; the caller writes text only. */
    readonly html?: string;
}

/**
 * One source string feeds both flavours, so the markdown a plain-text target
 * receives is the markdown the HTML was built from.
 */
export const buildRichCopyPayload = (
    source: string,
    resolutions: ICitationResolution[] | undefined,
    ctx: CitationLinkContext
): RichCopyPayload => {
    let text = source;

    try {
        text = stripAllEmojis(resolveCitationLinks(source, resolutions, ctx));
        return { text, html: markdownToClipboardHtml(text) };
    } catch {
        return { text };
    }
};
